import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { QuerySettingsProvider } from '@zenstackhq/tanstack-query/react';
import { Toaster } from 'sonner';
import App from './App';
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import { AuthProvider } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { RoleGate } from './auth/RoleGate';
import { authFetch } from './lib/api-fetch';
import { ThemeProvider } from './theme/ThemeContext';
import { GlobalLoadingBar } from './components/GlobalLoadingBar';
import { PwaUpdatePrompt } from './components/PwaUpdatePrompt';
import '@fontsource-variable/inter';
import '@fontsource-variable/space-grotesk';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './i18n';
import './index.css';
import AcceptInvitePage from './pages/AcceptInvitePage';
import { AppUIProvider } from './components/AppUIProvider';
import CustomerShell from './shells/CustomerShell';
import CustomerCallsPage from './pages/CustomerCalls';
import CustomerCallDetailPage from './pages/CustomerCalls/CallDetailPage';
import CustomerSettingsPage from './pages/CustomerSettings';
import CustomerSubscriptionPage from './pages/CustomerSubscription';
import AccountResolver from './account/AccountResolver';
import AccountScope from './account/AccountScope';
import AccountPickerPage from './pages/AccountPickerPage';
import TeamPage from './pages/Team';
import ContactsPage from './pages/Contacts';
import OnboardingPage from './pages/Onboarding';

// The back office is code-split: a customer on a phone never downloads it, which
// is the whole reason two shells beat one shell with role conditionals. Rollup
// pulls AdminShell and everything it imports into its own chunk.
const AdminShell = lazy(() => import('./shells/AdminShell'));

// The back-office PAGES are split too, not just the shell. They are the only
// consumers of the schema-driven table/form kit and tanstack-table, so leaving
// them eager would keep shipping the back office to every phone — the split
// would be cosmetic.
const UsersPage = lazy(() => import('./pages/Users'));
const AdminInvitePage = lazy(() => import('./pages/AdminInvitePage'));
const AdminCreateUserPage = lazy(() => import('./pages/AdminCreateUserPage'));
const InngestMonitorPage = lazy(() => import('./pages/InngestMonitor'));
const PhoneNumbersPage = lazy(() => import('./pages/PhoneNumbers'));
const AdminCallsPage = lazy(() => import('./pages/AdminCalls'));
const AdminAccountPage = lazy(() => import('./pages/AdminAccount'));
const AdminSubscriptionPage = lazy(() => import('./pages/AdminSubscription'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

// Two route trees behind one auth gate, one per audience. The shells share the
// providers and the component kit and nothing else — different navigation,
// different density, different device assumptions.
//
// Both are the same installed PWA: one manifest, one service worker, one origin.
// The role picks which tree mounts, so crossing between them is a client-side
// navigation rather than a page load.
const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      // Unauthenticated, and shared by both audiences — login is the one screen
      // that looks the same for a customer and for platform staff.
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/accept-invite', element: <AcceptInvitePage /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            // One place decides which half of the app a role belongs in. The shells
            // used to guard themselves, and two components each able to redirect at
            // the other ping-ponged between / and /admin in production.
            element: <RoleGate />,
            children: [
          // ── Customer app: mobile-first, bottom tabs ──
          //
          // Every customer screen lives under /a/:accountId, because one person can
          // own their own line and at the same time be a delegate in several others,
          // so "which account" is a property of the URL rather than of the session.
          // Two tabs then sit in two different lines without fighting, F5 returns
          // where you were, and a link to a call means the same thing for whoever you
          // send it to. docs/PWA/account-scoping-plan.md has the reasoning.
          //
          // `/` only redirects (it is the manifest's start_url, so the installed app
          // opens it) and `/accounts` is the picker. Neither ever redirects at the
          // other — see the RoleGate comment for why that matters.
          { path: '/', element: <AccountResolver /> },
          { path: '/accounts', element: <AccountPickerPage /> },
          {
            path: '/a/:accountId',
            element: <AccountScope />,
            children: [
              // The onboarding wizard sits INSIDE the account (it needs the line in
              // context) but OUTSIDE the shell — no bottom tabs while the line has no
              // number to answer on. CustomerShell redirects here when the account
              // still needs onboarding, and the wizard redirects back once it does
              // not; the two guard opposite conditions, so there is no ping-pong.
              { path: 'onboarding', element: <OnboardingPage /> },
              {
                element: <CustomerShell />,
                children: [
                  // Every tab in the bottom bar must resolve to something — an
                  // unrouted tab is a router 404, which reads as a broken app rather
                  // than an unfinished one. Each swaps for a real screen as its
                  // procedures land (the commented-out block in schema.zmodel);
                  // Team's are already there, its screen is not.
                  //
                  // Calls is a placeholder too, for now. `pages/Calls` is the clinic
                  // build's call log — a ResourceTable still reaching for specialists
                  // and booked appointments, neither of which exists in this product —
                  // so it showed a table of the wrong columns rather than this
                  // product's call list. It stays in the tree unrouted until the real
                  // screen (§4.4, the `calls`/`recordingUrl`/`markCallRead`
                  // procedures) replaces it; the placeholder is the honest state.
                  { index: true, element: <CustomerCallsPage /> },
                  { path: 'calls/:callId', element: <CustomerCallDetailPage /> },
                  { path: 'contacts', element: <ContactsPage /> },
                  { path: 'team', element: <TeamPage /> },
                  // Settings is real, not a placeholder: it holds sign-out, which the
                  // customer otherwise has no access to at all now that the sidebar
                  // (where it used to live) is admin-only.
                  { path: 'settings', element: <CustomerSettingsPage /> },
                  // Own-line usage, reached from Settings → Subscription & usage.
                  // OWNER-gated in the UI (viewBilling); the accountUsage procedure
                  // re-checks membership server-side.
                  { path: 'subscription', element: <CustomerSubscriptionPage /> },
                ],
              },
            ],
          },
          // ── Back office: desktop-first, left rail, lazily loaded ──
          {
            path: '/admin',
            element: (
              <Suspense fallback={null}>
                <AdminShell />
              </Suspense>
            ),
            children: [
              { index: true, element: <UsersPage /> },
              { path: 'users', element: <UsersPage /> },
              { path: 'phone-numbers', element: <PhoneNumbersPage /> },
              { path: 'calls', element: <AdminCallsPage /> },
              // Per-account hub (Overview / People / Number / AI assistant / Calls).
              // Reached from the Users list "Manage" action; keyed on accountId.
              { path: 'accounts/:accountId', element: <AdminAccountPage /> },
              { path: 'inngest', element: <InngestMonitorPage /> },
              { path: 'subscription', element: <AdminSubscriptionPage /> },
              { path: 'invite', element: <AdminInvitePage /> },
              { path: 'create-user', element: <AdminCreateUserPage /> },
            ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <GlobalLoadingBar />
        <AuthProvider>
          <QuerySettingsProvider value={{ endpoint: '/api', fetch: authFetch }}>
            <AppUIProvider>
              <RouterProvider router={router} />
            </AppUIProvider>
            <Toaster position="top-right" />
            <PwaUpdatePrompt />
          </QuerySettingsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
